import { useEffect, useRef, useState } from 'react'
import type React from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import { resolveChainPeerIds, resolveChainPeerStateById } from '../helpers/chain-peers'
import { computePolledCanvasSignature } from '../helpers/polled-canvas-signature'
import type { PolledCanvas } from '../types/polled-canvas'

const EMPTY_CANVAS: PolledCanvas = {
  shapes: [],
  selected: [],
  multiSelectChrome: null,
  selectionState: null,
  altHoveredShape: null,
  overflowBadgeShapes: [],
  chainPeerShapes: [],
  chainPeerStateById: new Map(),
}

/**
 * Live shapes and selection, sampled on an animation frame.
 *
 * A poll rather than a subscription because no single existing notification covers every path that
 * moves geometry — `onLiveSelectionGeometrySync` fires for selection transforms but not checkpoint
 * loads, undo, or remote multiplayer edits. Sampling on rAF means chrome tracks a drag within the
 * same frame the pointer handler wrote it (`shapesRef` is updated synchronously), and state is only
 * set when the fingerprint actually changes, so an idle canvas re-renders zero times.
 */
export function usePolledCanvas(
  shapeManagerRef: React.RefObject<ShapeManagerRef | null> | undefined
): PolledCanvas {
  const [canvas, setCanvas] = useState<PolledCanvas>(EMPTY_CANVAS)
  const signatureRef = useRef('')

  useEffect(() => {
    let frame = 0
    const tick = () => {
      const manager = shapeManagerRef?.current
      const next = manager?.getShapes?.() ?? []
      const selectedIds = manager?.getSelectedShapeIds?.() ?? []
      const chrome = manager?.getMultiSelectChrome?.() ?? null
      const selectionState = manager?.getChromeSelectionState?.() ?? null
      const altHoveredId = manager?.getAltHoveredShapeId?.() ?? null
      const overflowIds = manager?.getTextOverflowShapeIds?.() ?? new Set<string>()
      const editingShapeTextId = manager?.getEditingShapeTextId?.() ?? null
      const chainFrameIds = manager?.getChainFrameIds?.()
      const chainPeerIds = resolveChainPeerIds(chainFrameIds, selectedIds, editingShapeTextId)

      const signature = computePolledCanvasSignature({
        shapes: next,
        selectedIds,
        chrome,
        selectionState,
        altHoveredId,
        overflowIds,
        editingShapeTextId,
        chainPeerIds,
      })

      if (signature !== signatureRef.current) {
        signatureRef.current = signature

        const visible = next.filter((shape) => shape.hidden !== true)
        const selectedSet = new Set(selectedIds)
        const chainPeerShapes = visible.filter((shape) => chainPeerIds.has(shape.id))
        const chainPeerStateById = resolveChainPeerStateById(chainPeerShapes, next)

        setCanvas({
          shapes: visible,
          selected: visible.filter((shape) => selectedSet.has(shape.id)),
          multiSelectChrome: chrome,
          selectionState,
          altHoveredShape: visible.find((shape) => shape.id === altHoveredId) ?? null,
          // No selection gate. The badge used to require `isSelected || isEditing`, which meant the
          // last frame of a chain — the one place text is genuinely LOST rather than flowed onward —
          // hid the fact until someone happened to click it. `overflowIds` is already narrowed to
          // "no next link" by `isOverflowing` in `shape-text-editor/shape-text-editor.tsx`, so a mid-chain frame's
          // overset never reaches here; everything that does is text nobody can see.
          overflowBadgeShapes: visible.filter((shape) => overflowIds.has(shape.id)),
          chainPeerShapes,
          chainPeerStateById,
        })
      }

      frame = window.requestAnimationFrame(tick)
    }

    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [shapeManagerRef])

  return canvas
}
